// Dynamic Text Replacement (DTR) — campaign message-match variants.
//
// One config object, keyed by campaign slug. Each entry drives every DTR-aware
// hero surface for that variant: the eyebrow, the two-line headline, the
// subhead, and the trust strip (the 3 label/sub pairs at the bottom of the
// hero). A Message selection (template rail) — or, once wired, a `?campaign=`
// match — swaps them all in place together. Read the same way the trust strip already is
// (useDtrCampaign → campaign.<field>). Still on the seam for later: `heroImage`,
// `ctaLabel`.
//
// Eyebrows are removed across all six messages (Derek 2026-08-04 full hero
// pass) — the headline carries the message alone, and the fabricated "Join 1.4
// million homeowners …" social-proof frame is retired.
//
// LENGTH DISCIPLINE (important): DTR swaps this copy IN PLACE, so every variant
// must keep the SAME SHAPE and roughly the SAME LENGTHS as `default` or the
// swap visibly reflows the hero:
//   • eyebrow  — removed (empty string) on every message; the headline leads
//   • headline — exactly 2 lines; keep EACH line ≤ ~16 chars (matches
//                "What can an ADU" = 15) or it wraps at the 56px size
//   • subhead  — one sentence (~90-100 chars)
//   • trust    — exactly 3 items, short label (~9-16) + short sub (~16-24)

export const DEFAULT_KEY = 'default';

export const DTR_CAMPAIGNS = {
    // Shown when no campaign is matched — the generic, always-true baseline.
    // Display name is "ADU Standard" (not "Default"): every future product/landing
    // page has a baseline, so a product-specific name reads clearly in the Message
    // rail. The KEY stays `default` — it's the messageKey in the attribution/UTM
    // contract and the fallback used across the hero surfaces; only the label changed.
    default: {
        name: 'ADU Standard',
        // Optional per-variant cinematic hero background (Derek 2026-07-31). Set
        // to a public image path (e.g. '/images/hero/generational-living.jpg') on
        // the variants that should get a full-bleed photo; leave null/absent to
        // fall back to the hero gradient. Read by HeroStage; no video — image only.
        heroImage: null,
        // No eyebrow on the ADU Standard baseline (Derek 2026-08-04) — the headline
        // carries the message alone. Message-matched variants keep their eyebrow frame.
        eyebrow: '',
        headline: { line1: 'ADUs open new doors', line2: 'to more possibilities.' },
        // Plain-language ADU definition for a cold visitor who has never heard the
        // term — no ZIP/Report jargon, works across every flow (Derek 2026-08-04).
        subhead: 'An ADU is a small second home on your property — a backyard cottage, in-law suite, or studio.',
        trust: [
            { label: 'Zoning-aware', sub: 'Matched to your address' },
            { label: 'Personalized', sub: 'Based on your property' },
            { label: 'No guesswork', sub: 'Clear next steps' },
        ],
    },

    // Keeping families close — aging parents, adult children, caregivers.
    'generational-living': {
        name: 'Generational Living',
        eyebrow: '',
        // Campaign system: fixed anchor (line1) + variable persona payoff (line2).
        // Anchor "ADUs open new doors" is shared; the payoff angles it per persona.
        headline: { line1: 'ADUs open new doors', line2: 'for family coming home.' },
        subhead: 'See if your lot can add a private space for parents, grown kids, or guests — close, but still their own.',
        trust: [
            { label: 'Family-first', sub: 'Keep everyone close' },
            { label: 'Stay close', sub: 'Privacy, side by side' },
            { label: 'Room to grow', sub: 'A place for every age' },
        ],
    },

    // Help homeowners visualize what their property could become.
    'imagine-possibility': {
        name: 'Imagine Possibility',
        eyebrow: '',
        headline: { line1: 'ADUs open new doors', line2: 'to possibilities hiding in plain sight.' },
        subhead: 'See what your lot could become — the space, the options, and the potential already in your backyard.',
        trust: [
            { label: 'Picture it', sub: 'See what’s possible' },
            { label: 'Explore options', sub: 'Ideas worth exploring' },
            { label: 'Reimagine', sub: 'Your property, renewed' },
        ],
    },

    // An ADU as flexible lifestyle space — office, studio, guest suite, retreat.
    'lifestyle-studio': {
        name: 'Lifestyle Studio',
        eyebrow: '',
        headline: { line1: 'ADUs open new doors', line2: 'to a space that changes with you.' },
        subhead: 'See if your lot can fit a studio, office, guest suite, or quiet retreat — designed around your life.',
        trust: [
            { label: 'Your space', sub: 'Office, studio, retreat' },
            { label: 'Live your way', sub: 'Room for how you live' },
            { label: 'Made for you', sub: 'A space that adapts' },
        ],
    },

    // Uncover hidden value — usable space, rental income, equity, potential.
    'potential-unlocked': {
        name: 'Potential Unlocked',
        eyebrow: '',
        headline: { line1: 'ADUs open new doors', line2: 'to make your property work for you.' },
        subhead: 'See the value hiding on your property — usable space, rental income, and equity you can build.',
        trust: [
            { label: 'Hidden value', sub: 'Already on your lot' },
            { label: 'Rental income', sub: 'Put your lot to work' },
            { label: 'Build equity', sub: 'Unlock what you own' },
        ],
    },

    // Timeless idea — casitas, carriage houses, in-law flats; every road leads home.
    'all-roads-lead-home': {
        name: 'All Roads Lead to Home',
        eyebrow: '',
        headline: { line1: 'ADUs open new doors', line2: 'to the timeless idea of a place called home.' },
        subhead: 'Casita, carriage house, or in-law flat — see what a second home on your lot could look like today.',
        trust: [
            { label: 'Timeless idea', sub: 'New name, old wisdom' },
            { label: 'Every era', sub: 'Casitas to cottages' },
            { label: 'Always home', sub: 'A place of your own' },
        ],
    },
};

// Stable cycle order for the preview easter egg (object key insertion order).
export const DTR_ORDER = Object.keys(DTR_CAMPAIGNS);
